import { forwardRef, ImgHTMLAttributes } from 'react'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type MediaImageParams = {
  image_mobile: WPImage
  image_desktop: WPImage
  size: keyof WPImage['sizes']
  fit: 'cover' | 'contain'
  fill: boolean
  alt: string
}

export type MediaImageProps = ImgHTMLAttributes<HTMLImageElement> & MediaImageParams

const getImagePropsFromSize = (
  image: WPImage,
  size: MediaImageParams['size'],
  useSrcset = false
) => {
  if (!image || !image.sizes) {
    return
  }
  const source = image.sizes[size]
  const url = source.url || image.url
  const src = useSrcset ? undefined : url
  const srcSet = useSrcset ? url : undefined
  const width = source.width || image.width
  const height = source.height || image.height
  return { src, srcSet, width, height }
}

export const MediaImage = forwardRef<HTMLImageElement, MediaImageProps>(
  (
    {
      image_mobile,
      image_desktop,
      size = 'large',
      fit = 'cover',
      fill,
      alt,
      className,
      ...imageProps
    },
    ref
  ) => {
    const imageMobileProps = getImagePropsFromSize(image_mobile, size)
    const imageDesktopProps = getImagePropsFromSize(image_desktop, size, true)

    return (
      <picture>
        <source media="(min-width:1024px)" {...imageDesktopProps} />
        <img
          ref={ref}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={cn(
            'relative h-auto w-full',
            {
              'absolute left-0 top-0 h-full w-full': fill,
              'object-contain': fit === 'contain',
              'object-cover': fit === 'cover'
            },
            className
          )}
          {...imageProps}
          {...(imageMobileProps ? imageMobileProps : imageDesktopProps)}
        />
      </picture>
    )
  }
)

export default MediaImage
